/**
 * Admin: fetch full user profile and update user/iqama/pro qual/prev job (e.g. after phone follow-up)
 */

import { apiClient } from '../client'
import type { User } from './user.repository'
import type {
  UserIqamaDetailsRecord,
  UserProfessionalQualificationsRecord,
  UserPrevJobDetailsRecord,
  UserQuestionnaireAnswerRecord,
} from './user-self.repository'

export interface AdminUserFullProfile {
  user: User | null
  iqama: UserIqamaDetailsRecord | null
  professionalQualifications: UserProfessionalQualificationsRecord | null
  prevJob: UserPrevJobDetailsRecord | null
  questionnaireAnswers: UserQuestionnaireAnswerRecord[]
}

/** Allowed fields for admin user profile update */
export type AdminUpdateUserPayload = {
  en_name?: string
  ar_name?: string
  phone?: string
  city?: string
  nationality?: string
  dob?: string
  gender?: string
  how_did_you_know_us?: string
  internal_employ_name?: string
  job_type?: string
}

export const adminUserProfileRepository = {
  async getFullProfile(userId: number): Promise<AdminUserFullProfile> {
    const [user, iqamaRes, proQualRes, prevJobRes, answersRes] = await Promise.all([
      apiClient
        .get<{ data: User }>(`/api/admin/users/${userId}`)
        .then((r) => r.data)
        .catch(() => apiClient.get<User>(`/users/${userId}`).catch(() => null)),
      apiClient.get<{ data: UserIqamaDetailsRecord }>(`/api/user-iqama-details/user/${userId}`).catch(() => null),
      apiClient.get<{ data: UserProfessionalQualificationsRecord }>(`/api/user-professional-qualifications/user/${userId}`).catch(() => null),
      apiClient.get<{ data: UserPrevJobDetailsRecord }>(`/api/user-prev-job-details/user/${userId}`).catch(() => null),
      apiClient.get<{ data: UserQuestionnaireAnswerRecord[] }>(`/api/admin/user-profile/${userId}/questionnaire-answers`).catch(() => null),
    ])

    return {
      user: user ?? null,
      iqama: iqamaRes?.data ?? null,
      professionalQualifications: proQualRes?.data ?? null,
      prevJob: prevJobRes?.data ?? null,
      questionnaireAnswers: Array.isArray(answersRes?.data) ? answersRes.data : [],
    }
  },

  async updateUser(userId: number, data: AdminUpdateUserPayload): Promise<User> {
    const res = await apiClient.put<{ data: User }>(`/api/admin/users/${userId}`, data)
    return res.data
  },

  async updateIqama(id: number, data: Partial<UserIqamaDetailsRecord>): Promise<UserIqamaDetailsRecord> {
    const res = await apiClient.patch<{ data: UserIqamaDetailsRecord }>(`/api/user-iqama-details/${id}`, data)
    return res.data
  },

  async createIqama(data: Partial<UserIqamaDetailsRecord> & { user_id: number }): Promise<UserIqamaDetailsRecord> {
    const res = await apiClient.post<{ data: UserIqamaDetailsRecord }>('/api/user-iqama-details', data)
    return res.data
  },

  async updateProfessionalQualifications(
    id: number,
    data: Partial<UserProfessionalQualificationsRecord>
  ): Promise<UserProfessionalQualificationsRecord> {
    const res = await apiClient.patch<{ data: UserProfessionalQualificationsRecord }>(
      `/api/user-professional-qualifications/${id}`,
      data
    )
    return res.data
  },

  async createProfessionalQualifications(
    data: Partial<UserProfessionalQualificationsRecord> & { user_id: number }
  ): Promise<UserProfessionalQualificationsRecord> {
    const res = await apiClient.post<{ data: UserProfessionalQualificationsRecord }>(
      '/api/user-professional-qualifications',
      data
    )
    return res.data
  },

  async updatePrevJob(id: number, data: Partial<UserPrevJobDetailsRecord>): Promise<UserPrevJobDetailsRecord> {
    const res = await apiClient.patch<{ data: UserPrevJobDetailsRecord }>(`/api/user-prev-job-details/${id}`, data)
    return res.data
  },

  async createPrevJob(data: Partial<UserPrevJobDetailsRecord> & { user_id: number }): Promise<UserPrevJobDetailsRecord> {
    const res = await apiClient.post<{ data: UserPrevJobDetailsRecord }>('/api/user-prev-job-details', data)
    return res.data
  },

  /**
   * After creating a user (admin add-user flow), create one row per related table so the profile
   * matches what the edit dialog expects (same as saving each section when rows were missing).
   */
  async seedRelatedProfileRecordsAfterCreate(
    userId: number,
    opts?: {
      en_name?: string
      ar_name?: string
      phone?: string
      email?: string
      nationality?: string
      dob?: string | Date | null
      marital_status?: string
    }
  ): Promise<void> {
    const dobStr =
      opts?.dob == null || opts.dob === ''
        ? undefined
        : typeof opts.dob === 'string'
          ? opts.dob.slice(0, 10)
          : !Number.isNaN(new Date(opts.dob).getTime())
            ? new Date(opts.dob).toISOString().slice(0, 10)
            : undefined

    const iqamaPayload: Partial<UserIqamaDetailsRecord> & { user_id: number } = {
      user_id: userId,
      english_name_following_iqama: opts?.en_name?.trim() || undefined,
      arabic_name_following_iqama: opts?.ar_name?.trim() || undefined,
      nationality: opts?.nationality?.trim() || undefined,
      marital_status: opts?.marital_status?.trim() || undefined,
      birthdate: dobStr,
      mobile_no: opts?.phone?.trim() || undefined,
      personal_email: opts?.email?.trim() || undefined,
    }

    await Promise.all([
      this.createIqama(iqamaPayload).catch((e) => {
        console.warn('[adminUserProfile] seed iqama skipped or failed', e)
      }),
      this.createProfessionalQualifications({ user_id: userId }).catch((e) => {
        console.warn('[adminUserProfile] seed professional qualifications skipped or failed', e)
      }),
      this.createPrevJob({ user_id: userId }).catch((e) => {
        console.warn('[adminUserProfile] seed prev job skipped or failed', e)
      }),
    ])
  },
}
